import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { PromptBuilder } from 'components/index.js'
import ImageListType from './components/ImageListType'
import MediaType from './components/MediaType'
import MaskType from './components/MaskType'
import OptionType from './components/OptionType'
import TextType from './components/TextType'
import RangeType from './components/RangeType'
import ModalWrapper from 'components/Modals/ModalWrapper'
import AudioType from './components/AudioType'
import VideoType from './components/VideoType'
import { GoogleMapModal } from 'components/Modals'

const ModelInputRenderer = ({
  itemKey,
  label,
  description,
  name,
  type,
  fileType,
  value,
  onChange,
  options,
  selectImage,
  removeMedia = () => {},
  uploadFile,
  min,
  max,
  selectedModel,
  setSelectedModel,
  maskValue,
  selectedArgsExample,
  updateArgValues,
  uploadFilesMask,
  addDraggedImageToModel,
  negativePromptExamples,
  isSmall,
  initialUrl,
  optionView,
  fileInputRef: externalFileInputRef,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 600 })

  const [promptBuilderVisible, setPromptBuilderVisible] = useState(false)
  const [googleMapVisible, setGoogleMapVisible] = useState(false)
  const internalFileInputRef = useRef(null)
  const fileInputRef = externalFileInputRef || internalFileInputRef

  useEffect(() => {
    if (itemKey == 'seed' && value == 0) {
      onChange(1)
    }
  }, [itemKey])

  const currentOptionView = useMemo(
    () => optionView || (options?.length && typeof options[0] == 'string' ? 'dropdown-view' : 'box-view'),
    [optionView, options]
  )

  return (
    <>
      <div className="arg-field-container flex flex-col mt-2.5">
        {((type == 'string' && itemKey.toLowerCase() === 'prompt') ||
          ['property address', 'city', 'state'].includes(itemKey.toLowerCase())) && (
          <div className="mb-5">
            <button
              className="mb-2.5 md:mb-0 rounded-none md:rounded leading-3 font-bold cursor-pointer whitespace-nowrap bg-transparent border border-solid border-[#00000033] py-1.5 px-2 text-[#1a1725] text-[10px] h-[40px]"
              onClick={() =>
                itemKey.toLowerCase() === 'prompt' ? setPromptBuilderVisible(true) : setGoogleMapVisible(true)
              }
            >
              {itemKey.toLowerCase() === 'prompt' ? 'Prompt Builder' : 'Google Map'}
            </button>
          </div>
        )}

        {type === 'mask' || selectedModel.args[itemKey]?.tags?.includes('mask') ? (
          maskValue && (
            <MaskType
              itemKey={itemKey}
              value={value}
              maskValue={maskValue}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              uploadFilesMask={uploadFilesMask}
              isMobile={isMobile}
              isSmall={isSmall}
            />
          )
        ) : type === 'file' ? (
          fileType === 'audio' ? (
            <AudioType
              itemKey={itemKey}
              value={value}
              isMobile={isMobile}
              uploadFile={uploadFile}
              selectImage={selectImage}
              removeMedia={removeMedia}
              addDraggedImageToModel={addDraggedImageToModel}
              fileInputRef={fileInputRef}
            />
          ) : fileType == 'video' ? (
            <VideoType
              itemKey={itemKey}
              description={description}
              type={fileType}
              value={value}
              isMobile={isMobile}
              uploadFile={uploadFile}
              selectImage={selectImage}
              removeMedia={removeMedia}
              addDraggedImageToModel={addDraggedImageToModel}
              selectedArgsExample={selectedArgsExample}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              updateArgValues={updateArgValues}
              fileInputRef={fileInputRef}
            />
          ) : (
            <MediaType
              itemKey={itemKey}
              description={description}
              type={fileType || 'image'}
              value={value}
              isMobile={isMobile}
              uploadFile={uploadFile}
              selectImage={selectImage}
              removeMedia={removeMedia}
              addDraggedImageToModel={addDraggedImageToModel}
              selectedArgsExample={selectedArgsExample}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              updateArgValues={updateArgValues}
              fileInputRef={fileInputRef}
              initialUrl={initialUrl}
            />
          )
        ) : type === 'image_list' ? (
          <ImageListType
            itemKey={itemKey}
            description={description}
            value={value}
            isMobile={isMobile}
            uploadFile={uploadFile}
            selectImage={selectImage}
            removeMedia={removeMedia}
            addDraggedImageToModel={addDraggedImageToModel} // to refactor
            selectedArgsExample={selectedArgsExample} // to refactor
            updateArgValues={updateArgValues}
            fileInputRef={fileInputRef}
          />
        ) : type === 'option' || type === 'multioption' ? (
          <OptionType
            name={name}
            itemKey={itemKey}
            type={type}
            label={label}
            optionView={currentOptionView}
            options={options}
            value={value}
            onChange={onChange}
            updateArgValues={updateArgValues}
          />
        ) : ['number', 'float', 'array', 'integer'].includes(type) ? (
          <RangeType itemKey={itemKey} type={type} value={value} min={min} max={max} onChange={onChange} />
        ) : (
          <TextType
            itemKey={itemKey}
            type={type}
            name={name}
            negativePromptExamples={negativePromptExamples}
            selectedArgsExample={selectedArgsExample}
            value={value}
            updateArgValues={updateArgValues}
            onChange={onChange}
          />
        )}
      </div>
      {promptBuilderVisible && (
        <ModalWrapper
          onClose={() => setPromptBuilderVisible(false)}
          classNames="image-generation-model h-[50%] md:h-[96%] w-[91%] md:w-[80%] !py-0 5 px-2 md:px-4 !pt-0 rounded-none !justify-start"
        >
          <PromptBuilder
            onConfirm={(value) => {
              updateArgValues(itemKey, value)
              setPromptBuilderVisible(false)
            }}
          />
        </ModalWrapper>
      )}
      {googleMapVisible && (
        <GoogleMapModal
          onClose={() => setGoogleMapVisible(false)}
          addressType={itemKey.toLowerCase()}
          onConfirm={(value) => {
            updateArgValues(itemKey, value)
            setGoogleMapVisible(false)
          }}
        />
      )}
    </>
  )
}

export default ModelInputRenderer
